import { FunctionComponent } from 'react';
import { isByocDataset, Layer, isUsWestDem } from '../../meta/types/wms/Layer';
import {
  AWS_EU_CENTRAL_1,
  AWS_US_WEST_2,
  ByocCollectionsCache,
  CREODIAS,
  MUNDI,
} from '../../meta/types/byoc';
import USWestWarning from '../../components/USWestWarning';
import CreodiasWarning from '../../components/CreodiasWarning';
import MundiWarning from '../../components/MundiWarning';
import { CodeDeCreoByocWarning, CreoEUCentralWarning, CreoUSWestWarning } from './extWarnings';
import {
  AWS_EU_CENTRAL_1_COLLECTIONS,
  AWS_US_WEST_2_COLLECTIONS,
  CREODIAS_COLLECTIONS,
} from './producerCollections';
import { DatasetId } from '../../meta/types/wms/Dataset';

const getByocLayerLocation = (layer: Layer, cache: ByocCollectionsCache) => {
  const collectionCache = cache[layer.datasourceDefaults.collectionId!];
  return collectionCache?.collection.location ?? false;
};

const containsLocationLayer = (
  layers: Layer[],
  byocCollectionsCache: ByocCollectionsCache,
  collectionsWithLocation: { location: string; collections: Set<DatasetId> },
) => {
  return layers.some(layer => {
    const datasetId = layer.datasourceDefaults.type;
    if (isByocDataset(datasetId)) {
      return getByocLayerLocation(layer, byocCollectionsCache) === collectionsWithLocation.location;
    }
    if (datasetId === DatasetId.DEM) {
      const { demInstance } = layer.datasourceDefaults;
      if (isUsWestDem(demInstance)) {
        return collectionsWithLocation.location === 'aws-us-west-2';
      }
      return collectionsWithLocation.location === 'aws-eu-central-1';
    }
    return collectionsWithLocation.collections.has(datasetId);
  });
};

export const getSinergiseLayersWarnings = (
  layers: Layer[],
  byocCollectionsCache: ByocCollectionsCache,
): FunctionComponent[] => {
  const warnings = [];

  const hasUsWestLayer = containsLocationLayer(layers, byocCollectionsCache, {
    location: AWS_US_WEST_2,
    collections: AWS_US_WEST_2_COLLECTIONS,
  });

  const hasCreodiasLayer = containsLocationLayer(layers, byocCollectionsCache, {
    location: CREODIAS,
    collections: CREODIAS_COLLECTIONS,
  });

  const hasMundiLayer = containsLocationLayer(layers, byocCollectionsCache, {
    location: MUNDI,
    collections: CREODIAS_COLLECTIONS,
  });

  if (hasUsWestLayer) {
    warnings.push(USWestWarning);
  }

  if (hasCreodiasLayer) {
    warnings.push(CreodiasWarning);
  }

  if (hasMundiLayer) {
    warnings.push(MundiWarning);
  }

  return warnings;
};

export const getCreoDiasWarnings = (
  layers: Layer[],
  byocCollectionsCache: ByocCollectionsCache,
): FunctionComponent[] => {
  const warnings = [];

  const hasUsWestLayer = containsLocationLayer(layers, byocCollectionsCache, {
    location: AWS_US_WEST_2,
    collections: AWS_US_WEST_2_COLLECTIONS,
  });
  const hasEuCentralLayer = containsLocationLayer(layers, byocCollectionsCache, {
    location: AWS_EU_CENTRAL_1,
    collections: AWS_EU_CENTRAL_1_COLLECTIONS,
  });

  if (hasEuCentralLayer) {
    warnings.push(CreoEUCentralWarning);
  }

  if (hasUsWestLayer) {
    warnings.push(CreoUSWestWarning);
  }

  return warnings;
};

export const getCodeDeWarnings = (
  layers: Layer[],
  byocCollectionsCache: ByocCollectionsCache,
): FunctionComponent[] => {
  const warnings = [];

  const byocLayers = layers.filter(layer => isByocDataset(layer.datasourceDefaults.type));

  const hasCreoLayer = containsLocationLayer(byocLayers, byocCollectionsCache, {
    location: CREODIAS,
    collections: CREODIAS_COLLECTIONS,
  });

  if (hasCreoLayer) {
    warnings.push(CodeDeCreoByocWarning);
  }

  return warnings;
};
